/**
 * Shared API types.
 *
 * docs/06-FRONTEND-SPEC.md §9 — entity types (News, Program, Staff…) are
 * **generated** from the OpenAPI spec, never hand-written, so that the frontend
 * and backend cannot drift. They live in `src/generated/api.ts`, are produced
 * by `pnpm generate:api-types`, and CI fails when they no longer match the
 * routes.
 *
 * What lives here by hand is only the envelope: the structural contract from
 * docs/00-PROJECT-BRIEF.md §5.6 and docs/04-API-SPEC.md §1, which describes
 * the shape *around* every resource rather than any resource itself.
 */

export type { components, operations, paths } from './generated/api.js';

import type { components, operations, paths } from './generated/api.js';

/**
 * The response body of an endpoint, by operation id.
 *
 * `ApiResponse<'v1.news.show'>` rather than an entity name, because the
 * generated document keys on operations — and an operation is what the
 * frontend actually calls.
 */
export type ApiResponse<
  Id extends keyof operations,
  Status extends keyof operations[Id]['responses'] = 200 extends keyof operations[Id]['responses']
    ? 200
    : keyof operations[Id]['responses'],
> = operations[Id]['responses'][Status] extends {
  content: { 'application/json': infer Body };
}
  ? Body
  : never;

/** A named schema from the generated document, e.g. `Schema<'NewsResource'>`. */
export type Schema<Name extends keyof components['schemas']> = components['schemas'][Name];

/** Every path the API serves, as a union of string literals. */
export type ApiPath = keyof paths;

/** docs/00-PROJECT-BRIEF.md §3 — only `ka` and `en` are populated in v1. */
export const LOCALES = ['ka', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'ka';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** A translatable field as the admin API sends and receives it. */
export type Translated<T = string> = Partial<Record<Locale, T>>;

/**
 * Every translatable resource carries these, so the UI can render the
 * "not available in English" notice instead of silently serving Georgian
 * (docs/00-PROJECT-BRIEF.md §5.2).
 */
export interface TranslationMeta {
  translations_available: Locale[];
  locale: Locale;
  is_fallback: boolean;
}

/** Single-resource envelope. */
export interface ApiResource<T> {
  data: T;
}

/** Collection envelope — docs/04-API-SPEC.md §1. */
export interface ApiCollection<T> {
  data: T[];
  meta: PaginationMeta;
  links: PaginationLinks;
}

export interface PaginationMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

export interface PaginationLinks {
  first: string | null;
  last: string | null;
  prev: string | null;
  next: string | null;
}

/**
 * Error envelope. Laravel's own shape: a message, plus per-field messages on
 * a 422.
 */
export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

/** Query parameters every index endpoint accepts (docs/04-API-SPEC.md §1). */
export interface ListParams {
  page?: number;
  /** Server caps this at 100. */
  per_page?: number;
  search?: string;
  /** A leading `-` means descending, e.g. `-published_at`. */
  sort?: string;
  locale?: Locale;
}

/** docs/08-INFRA-DEVOPS.md §7 — GET /api/v1/health. */
export interface HealthCheck {
  ok: boolean;
  error: string | null;
}

export interface HealthReport {
  status: 'ok' | 'degraded';
  checks: Record<'database' | 'cache' | 'storage' | 'queue', HealthCheck>;
  app: { environment: string; debug: boolean };
  checked_at: string;
}
